import * as React from "react";
import { Box, Typography, Stack, TextField, Button, IconButton, CircularProgress, Alert } from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import SettingsInputAntennaRoundedIcon from "@mui/icons-material/SettingsInputAntennaRounded";
import { fetchMyUplinkStatus, unlockUplink } from "../api/queries";
import type { UplinkStatus } from "../types/workshop";

// Where a group lands after submitting its mission plan: the sheet is locked
// now, the group goes off to build its hardware, and comes back here to open
// the command uplink with the group's name and its uplink code -- the same
// way the sheet itself was opened, just with the second code.
export default function UplinkWaiting(props: { handlePageChange: (page: string) => void }) {
  const { handlePageChange } = props;

  const [status, setStatus] = React.useState<UplinkStatus | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [groupName, setGroupName] = React.useState("");
  const [code, setCode] = React.useState("");
  const [checking, setChecking] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let mounted = true;

    fetchMyUplinkStatus().then(({ data, error: fetchError }) => {
      if (!mounted) return;

      // Not in a group yet, or the plan isn't submitted: the mission sheet
      // decides where to go from there.
      if (fetchError || !data || !data.plan_submitted) {
        handlePageChange("MissionView");
        return;
      }

      if (data.uplink_unlocked) {
        handlePageChange("CommandExecutor");
        return;
      }

      setLoading(false);
      setStatus(data);
      setGroupName(data.group_name);
    });

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitCode = async () => {
    if (!groupName.trim() || !code.trim()) return;
    setError(null);
    setChecking(true);

    const { data: ok, error: verifyError } = await unlockUplink(groupName.trim(), code.trim());

    setChecking(false);

    if (verifyError) {
      setError(verifyError.message);
      return;
    }

    if (!ok) {
      setError("That group name and uplink code don't match. Please double-check both and try again.");
      return;
    }

    handlePageChange("Launch");
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: { xs: 2, sm: 3 },
        py: { xs: 4, md: 6 },
        bgcolor: "#08050b",
        backgroundImage:
          "linear-gradient(90deg, rgba(9, 5, 11, 0.96) 0%, rgba(9, 5, 11, 0.84) 42%, rgba(80, 29, 127, 0.2) 100%), url('/background.png')",
        backgroundSize: "cover",
        backgroundPosition: { xs: "62% center", md: "center" },
      }}
    >
      {loading ? (
        <CircularProgress sx={{ color: "#C4B5FD" }} />
      ) : (
        <Box
          sx={{
            width: "100%",
            maxWidth: 480,
            p: { xs: 2.75, sm: 3.5 },
            borderRadius: 3,
            border: "1px solid rgba(168,85,247,0.28)",
            background: "linear-gradient(160deg, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0.02) 100%)",
            backdropFilter: "blur(14px)",
          }}
        >
          <Stack spacing={2.5}>
            <IconButton
              onClick={() => handlePageChange("Dashboard")}
              aria-label="Back to dashboard"
              sx={{
                alignSelf: "flex-start",
                color: "#E9D5FF",
                border: "1px solid rgba(168,85,247,0.35)",
                bgcolor: "rgba(255,255,255,0.04)",
                "&:hover": { bgcolor: "rgba(168,85,247,0.16)" },
              }}
            >
              <ArrowBackRoundedIcon />
            </IconButton>

            <Stack spacing={1.5} alignItems="center" textAlign="center">
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 2.5,
                  display: "grid",
                  placeItems: "center",
                  color: "#E9D5FF",
                  background: "linear-gradient(160deg, rgba(124,58,237,0.45), rgba(56,189,248,0.22))",
                  border: "1px solid rgba(196,181,253,0.35)",
                }}
              >
                <SettingsInputAntennaRoundedIcon sx={{ fontSize: 28 }} />
              </Box>

              <Typography sx={{ color: "#FFFFFF", fontWeight: 900, fontSize: { xs: 20, sm: 23 } }}>
                Mission plan submitted!
              </Typography>
              <Typography sx={{ color: "rgba(226,232,240,0.75)", fontSize: 14, lineHeight: 1.6 }}>
                Group <strong style={{ color: "#E9D5FF" }}>{status?.group_name}</strong> has locked in its plan for mission{" "}
                <strong style={{ color: "#E9D5FF" }}>{status?.mission_name}</strong>. Now it's time to design your satellite's
                hardware together, in the hardware design tool our team shows you.
              </Typography>
              <Typography sx={{ color: "rgba(226,232,240,0.75)", fontSize: 14, lineHeight: 1.6 }}>
                Come back here once your hardware is ready: enter your group's name and the uplink code you receive from our
                team. Then it's time to launch your satellite.
              </Typography>
            </Stack>

            {error && (
              <Alert severity="error" variant="filled" onClose={() => setError(null)} sx={{ borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            <TextField
              label="Group name"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder="Enter your group's name"
              fullWidth
              onKeyDown={(e) => {
                if (e.key === "Enter") submitCode();
              }}
              sx={{
                "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#FFFFFF" },
                "& .MuiInputLabel-root": { fontWeight: 800, color: "#374151" },
              }}
            />

            <TextField
              label="Uplink code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Enter the uplink code your group received"
              fullWidth
              onKeyDown={(e) => {
                if (e.key === "Enter") submitCode();
              }}
              sx={{
                "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#FFFFFF" },
                "& .MuiInputLabel-root": { fontWeight: 800, color: "#374151" },
              }}
            />

            <Button
              variant="contained"
              size="large"
              onClick={submitCode}
              disabled={checking || !groupName.trim() || !code.trim()}
              sx={{ bgcolor: "#7C3AED", "&:hover": { bgcolor: "#6D28D9" }, textTransform: "none", fontWeight: 800, borderRadius: 2 }}
            >
              {checking ? <CircularProgress size={20} sx={{ color: "inherit" }} /> : "Let's launch our satellite!"}
            </Button>
          </Stack>
        </Box>
      )}
    </Box>
  );
}
